import type { App } from 'vue';
import { ref, h, createApp, defineComponent } from 'vue';
import type { RecaptchaInfo } from '@/types/AuthType';

// COMPONENTS
import { GConfigProvider } from '@flash-global66/g-config-provider';
import Otp from '../views/Otp/index.vue';

// CONFIG
import VueReCaptcha from '@/lib/vue-recaptcha';
import router from '@/router';

// TYPES
import type {
  BusinessFlowType,
  INextOtp,
  OtpChannelType,
  ShippingOtp,
  OtpFlowConfigParams,
} from '../types/otp.type';
import {
  BusinessFlow,
  OtpChannelEnum,
  RequestTypeEnum
} from '../types/otp.type';

const defaultChannelType = OtpChannelEnum.WHATSAPP;

const isVisible = ref(false);
const hideInputs = ref<ShippingOtp[]>([]);
const channelType = ref<OtpChannelType>(defaultChannelType);
const requestType = ref<RequestTypeEnum>(RequestTypeEnum.WITH_SESSION);
const businessFlow = ref<BusinessFlowType>(BusinessFlow.SIGN_IN_B2B);
const shouldShowFirstButtons = ref(false);
const onNext = ref<(payload: INextOtp) => Promise<void>>(async () => {});
const onRequest = ref<
  | ((type: ShippingOtp, recaptchaInfo?: RecaptchaInfo) => Promise<void>)
  | null
>(null);
const error = ref('');
const hasLoaderHandler = ref(false);
const shouldMountRecaptcha = ref(false);

const appInstance = ref<App<Element> | null>(null); // Almacena la instancia de la app
const containerInstance = ref<HTMLElement | null>(null); // Almacena el contenedor

export const useOtpFlow = () => {
  /**
   * Configura el flujo de validación OTP antes de su visualización.
   * Este método debe invocarse antes de `showOtp()` y limpia cualquier estado previo para garantizar un inicio limpio.
   *
   * @param {OtpFlowConfigParams} flowConfig - Objeto de configuración para el flujo.
   * @param {OtpChannelEnum} flowConfig.channel - Canal por defecto para el envío del código (WHATSAPP, SMS, EMAIL).
   * @param {ShippingOtp[]} [flowConfig.inputsToHide] - Canales a ocultar en la interfaz de selección.
   * @param {RequestTypeEnum} [flowConfig.request] - Tipo de solicitud que determina el comportamiento del servicio.
   * @param {BusinessFlowType} [flowConfig.businessFlow] - Contexto de negocio para el cual se solicita el OTP.
   * @param {boolean} [flowConfig.showFirstButtons] - Si es `true`, muestra primero la selección de canal en lugar del input de código.
   */
  const setFlowConfig = (flowConfig: OtpFlowConfigParams) => {
    cleanseState();

    channelType.value = flowConfig.channel || defaultChannelType;
    hideInputs.value = flowConfig.inputsToHide || [];
    requestType.value = flowConfig.request || RequestTypeEnum.WITH_SESSION;
    businessFlow.value = flowConfig.businessFlow || BusinessFlow.SIGN_IN_B2B;
    shouldShowFirstButtons.value = flowConfig.showFirstButtons ?? false;
    hasLoaderHandler.value = flowConfig.hasLoaderHandler || false;
    shouldMountRecaptcha.value = flowConfig.shouldMountRecaptcha || false;
  };

  /**
   * Define la función callback que se ejecutará tras una validación de OTP exitosa.
   *
   * @param {(payload: INextOtp) => Promise<void>} method - La función asíncrona a ejecutar. Recibe un payload con el OTP y el canal utilizado.
   */
  const setOnNext = (method: (payload: INextOtp) => Promise<void>) => {
    onNext.value = method;
  };

  /**
   * Define la función callback que se ejecutará para generar el OTP.
   * Permite inyectar una lógica de solicitud OTP personalizada, desacoplada del servicio interno del módulo.
   *
   * @param {(type: ShippingOtp) => Promise<void>} method - Función asíncrona a ejecutar para solicitar el OTP.
   */
  const setOnRequest = (
    method: (type: ShippingOtp, recaptchaInfo?: RecaptchaInfo) => Promise<void>
  ) => {
    onRequest.value = method;
  };

  /**
   * Crea y monta una nueva instancia de la "mini-app" de OTP en el DOM.
   * Este método lanza la interfaz de usuario modal a pantalla completa y debe ser llamado después de `setFlowConfig()`.
   *
   * @returns {Promise<boolean>} Una promesa que se resuelve a `true` cuando el componente se cierra, ya sea por éxito, cancelación o programáticamente.
   */
  const showOtp = () => {
    isVisible.value = true;

    return new Promise((resolve) => {
      let isClosed = false;

      const container = document.createElement('div');
      document.body.appendChild(container);

      const onClose = () => {
        if (isClosed) return;
        isClosed = true;

        hideOtp();
        resolve(true);
      };

      const Wrapper = defineComponent({
        name: 'Otp',
        setup() {
          return () =>
            h(GConfigProvider, null, {
              default: () =>
                h(Otp, {
                  onClose,
                }),
            });
        },
      });

      const app = createApp(Wrapper);
      app.use(router);
      VueReCaptcha(app);

      appInstance.value = app;
      containerInstance.value = container;

      app.mount(container);
    });
  };

  /**
   * Cierra y destruye la instancia del componente OTP de forma programática.
   * Se encarga de la limpieza completa del estado del composable y de los elementos del DOM.
   */
  const hideOtp = () => {
    cleanseState();
  };

  /**
   * Asigna un mensaje de error para ser mostrado en la interfaz del componente OTP.
   *
   * @param {string} err - El mensaje de error a mostrar, típicamente en el input del código.
   */
  const setOtpError = (err) => {
    error.value = err;
  };

  const cleanseState = () => {
    isVisible.value = false;
    hideInputs.value = [];
    channelType.value = defaultChannelType;
    businessFlow.value = BusinessFlow.SIGN_IN_B2B;
    requestType.value = RequestTypeEnum.WITH_SESSION;
    shouldShowFirstButtons.value = false;
    hasLoaderHandler.value = false;
    shouldMountRecaptcha.value = false;
    onNext.value = async () => {};
    onRequest.value = null;
    error.value = '';

    checkAndDestroyAppInstance();
  };

  const checkAndDestroyAppInstance = () => {
    if (appInstance.value) {
      appInstance.value.unmount();
      appInstance.value = null;
    }
    if (containerInstance.value && containerInstance.value.parentNode) {
      containerInstance.value.parentNode.removeChild(
        containerInstance.value as HTMLElement
      );
      containerInstance.value = null;
    }
  };

  return {
    setFlowConfig,
    showOtp,
    hideOtp,
    setOnNext,
    setOnRequest,
    setOtpError,
    hideInputs,
    channelType,
    requestType,
    businessFlow,
    shouldShowFirstButtons,
    error,
    isVisible,
    onNext,
    onRequest,
    hasLoaderHandler,
    shouldMountRecaptcha,
  };
};
